import { useEffect, useState } from 'react';
import { BellRing, X } from 'lucide-react';
import { Button } from '@hrms/ui';
import {
  currentPushPermission,
  hasInteractedWithPushPrompt,
  isPushSupported,
  isWebPushConfigured,
  markPushPromptInteracted,
  maybeSilentlyResubscribe,
  shouldShowPushPrompt,
  subscribeAndRegisterPush,
} from '../../lib/webpush.js';

/**
 * The ONE-TIME browser push permission prompt (PR7 of the notification module rebuild) — see
 * `lib/webpush.ts`'s header for the full design rationale. There is deliberately no other entry
 * point to this anywhere in the product: no settings page, no toggle, no re-ask.
 *
 * Mounted once in `AppShell`, next to `ChangePasswordBanner` — same reasoning as that one: it
 * needs to render above every routed screen for whichever employee happens to see it, exactly
 * once, and then never again for that browser.
 */
export function PushPermissionBanner() {
  const [visible, setVisible] = useState(false);
  const [enabling, setEnabling] = useState(false);

  // Decide once, on mount, whether the one-time prompt has anything left to offer. Permission and
  // the interacted flag do not change from underneath this component while it is mounted (both
  // only change via this component's own click handlers, or via the browser's own settings UI,
  // which the SILENT re-check below — not this banner — is what reacts to).
  useEffect(() => {
    setVisible(
      isWebPushConfigured() &&
        shouldShowPushPrompt(
          isPushSupported(),
          currentPushPermission(),
          hasInteractedWithPushPrompt(),
        ),
    );
  }, []);

  // The silent, no-UI half of the flow: mount + visibilitychange only, per the task's explicit
  // "do NOT poll on a timer" instruction — see `maybeSilentlyResubscribe`'s own header.
  useEffect(() => {
    void maybeSilentlyResubscribe();
    function onVisibilityChange() {
      if (document.visibilityState === 'visible') void maybeSilentlyResubscribe();
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  if (!visible) return null;

  async function handleEnable() {
    setEnabling(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        await subscribeAndRegisterPush();
      }
      // Interacted either way — a denial here is exactly the "cannot be re-requested by script"
      // case rule 6 describes, and a granted-but-failed subscribe is what the silent re-check
      // above exists to retry later, not something this one-time banner should keep asking about.
    } finally {
      markPushPromptInteracted();
      setEnabling(false);
      setVisible(false);
    }
  }

  function handleDismiss() {
    markPushPromptInteracted();
    setVisible(false);
  }

  return (
    <div
      role="status"
      className="flex items-center justify-between gap-3 border-b border-accent/30 bg-accent-subtle px-4 py-2 text-sm text-fg sm:px-6"
    >
      <div className="flex min-w-0 items-center gap-2">
        <BellRing className="size-4 shrink-0 text-accent" aria-hidden="true" />
        <span className="truncate">
          Turn on browser notifications to hear about approvals and updates as they happen.
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button variant="primary" size="sm" onClick={handleEnable} loading={enabling}>
          Enable
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDismiss}
          disabled={enabling}
          aria-label="Dismiss"
        >
          <X className="size-4" />
        </Button>
      </div>
    </div>
  );
}
